import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { TextField, Button, Box } from '@mui/material';
import FormSkeleton from '@components/FormSkeleton';

const PackForm = forwardRef(({ initialData, onSubmit, buttons }, ref) => {
    const { t } = useTranslation();
    const [loaded, setLoaded] = useState(false);

    const validationSchema = Yup.object({
        quantity: Yup.number()
            .typeError(t('Must be a number'))
            .required(t('This field is required'))
            .min(1, t('Must be greater than or equal to {{min}}', { min: 1 })),
    });

    const { control, handleSubmit, setValue, setError, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        reValidateMode: 'onBlur',
    });
    useImperativeHandle(ref, () => ({ setError }));

    useEffect(() => {
        if (initialData) {
            for (const [key, value] of Object.entries(initialData)) {
                setValue(key, value);
            }
        }
        setLoaded(true);
    }, [initialData, setValue]);

    const handleFormSubmit = (data) => {
        if (onSubmit) {
            onSubmit(data);
        }
    };

    if (!loaded) return <FormSkeleton nb={3} />;

    return (
        <form onSubmit={handleSubmit(handleFormSubmit)}>
            <Box mb={3}>
                <Controller
                    name="quantity"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            required
                            fullWidth
                            type="number"
                            label={t('Quantity')}
                            variant="outlined"
                            error={Boolean(errors.quantity)}
                            helperText={errors.quantity?.message}
                            inputProps={{ min: 0 }}
                        />
                    )}
                />
            </Box>

            <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button type="submit" variant="contained" color="primary">
                        {t('Save')}
                    </Button>
                </Box>
                {buttons}
            </Box>
        </form>
    );
});

export default PackForm;